import { ApolloClient, gql } from 'apollo-boost';
import {
  ReportStage,
  FeedbackStatus,
} from 'donor/portal/pages/FeedbackDashboard/api';
import { capitalizeOnlyFirstLetter } from 'shared/utils/stringUtils';
import { SurveyFromAPI } from 'admin/pages/ManageAccounts/CustomQuestions/api';
import { ReportingType } from '../../../ReportsBeta/NewReport/api';

const mutation = gql`
  mutation updateReport(
    $reportId: Int!
    $status: String!
    $stage: String
    $optedInFullReporting: Boolean
    $reportingType: String
    $version: String = "v4"
  ) {
    updateReport(
      id: $reportId
      status: $status
      stage: $stage
      optedInFullReporting: $optedInFullReporting
      reportingType: $reportingType
      version: $version
    ) {
      success
    }
  }
`;

const updateReportStartAndEndDateMutation = gql`
  mutation updateReport(
    $reportId: Int!
    $startDate: String!
    $endDate: String!
    $version: String = "v4"
  ) {
    updateReport(
      id: $reportId
      startDate: $startDate
      endDate: $endDate
      version: $version
    ) {
      success
    }
  }
`;

const updateReportVersionMutation = gql`
  mutation updateReport($reportId: Int!, $version: String = "v4") {
    updateReport(id: $reportId, version: $version) {
      success
    }
  }
`;

const adminMutation = gql`
  mutation adminUpdateReportTags(
    $reportId: Int!
    $status: String!
    $stage: String
    $reportingType: String
    $sendNotifications: Boolean
    $optedInFullReporting: Boolean
  ) {
    adminUpdateReportTags(
      id: $reportId
      status: $status
      stage: $stage
      reportingType: $reportingType
      sendNotifications: $sendNotifications
      optedInFullReporting: $optedInFullReporting
    ) {
      success
    }
  }
`;

const mutationUpdateYear = gql`
  mutation updateReport($reportId: Int!, $year: Int, $version: String = "v4") {
    updateReport(id: $reportId, year: $year, version: $version) {
      success
    }
  }
`;

const mutationUpdateReportName = gql`
  mutation updateReport(
    $reportId: Int!
    $name: String!
    $version: String = "v4"
  ) {
    updateReport(id: $reportId, name: $name, version: $version) {
      success
    }
  }
`;

const mutationUpdateReportingType = gql`
  mutation updateReport(
    $reportId: Int!
    $reportingType: String!
    $status: String
    $stage: String
    $optedInFullReporting: Boolean
    $version: String = "v4"
  ) {
    updateReport(
      id: $reportId
      reportingType: $reportingType
      status: $status
      stage: $stage
      optedInFullReporting: $optedInFullReporting
      version: $version
    ) {
      success
    }
  }
`;

const mutationOptInFullReporting = gql`
  mutation updateReport(
    $reportId: Int!
    $optedInFullReporting: Boolean!
    $version: String = "v4"
  ) {
    updateReport(
      id: $reportId
      optedInFullReporting: $optedInFullReporting
      version: $version
    ) {
      success
    }
  }
`;

const mutationUpdateReportLevel1DonorName = gql`
  mutation updateReport(
    $reportId: Int!
    $level1DonorName: String!
    $visibility: String
  ) {
    updateReport(
      id: $reportId
      level1DonorName: $level1DonorName
      visibility: $visibility
    ) {
      success
      error
    }
  }
`;

const mutationUpdateReportLevel1Budget = gql`
  mutation updateReport($reportId: Int!, $level1Budget: String!) {
    updateReport(id: $reportId, level1Budget: $level1Budget) {
      success
      error
    }
  }
`;

const mutationUpdateReportLevel1ReachSucceedMatch = gql`
  mutation updateReport($reportId: Int!, $level1ReachSucceedMatch: String!) {
    updateReport(
      id: $reportId
      level1ReachSucceedMatch: $level1ReachSucceedMatch
    ) {
      success
      error
    }
  }
`;

const customQuestionsQuery = gql`
  query allReportSurvey($reportId: Int!) {
    allReportSurvey(filters: { reportId: $reportId }) {
      edges {
        node {
          dbId
          name
          description
          donorId
          portfolioId
          enabled
          years
          stage
          portfolio {
            portfolioId
            accountId
            accountName
            name
            description
            year
            years
            status
            customQuestions
          }
          questions {
            edges {
              node {
                dbId
                title
                description
                nextQuestionId
                required
                questionType
                state
                section
                options {
                  edges {
                    node {
                      dbId
                      text
                      value
                    }
                  }
                }
                answers {
                  edges {
                    node {
                      dbId
                      value
                      optionId
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const updateReportStateAndEndDates = (
  client: ApolloClient<object>,
  reportId: number | string,
  startDate: string,
  endDate: string,
) => {
  return client.mutate({
    mutation: updateReportStartAndEndDateMutation,
    variables: {
      reportId,
      startDate,
      endDate,
    },
    fetchPolicy: 'no-cache',
  });
};

export const submitAdminReport = (
  client: ApolloClient<object>,
  reportId: number | string,
  status: FeedbackStatus,
  stage?: ReportStage,
  sendNotifications?: boolean,
  reportingType?: 'FULL' | 'LIGHT' | 'SUPER_LIGHT',
  optedInFullReporting?: boolean,
) => {
  return client.mutate({
    mutation: adminMutation,
    variables: {
      reportId,
      status,
      stage: stage ? capitalizeOnlyFirstLetter(stage) : undefined,
      reportingType,
      sendNotifications,
      optedInFullReporting,
    },
    fetchPolicy: 'no-cache',
  });
};

export const submitReport = (
  client: ApolloClient<object>,
  reportId: number | string,
  status: FeedbackStatus,
  stage?: ReportStage,
  optedInFullReporting = false,
  reportingType?: ReportingType,
) => {
  return client.mutate({
    mutation,
    variables: {
      reportId,
      status,
      stage: stage ? capitalizeOnlyFirstLetter(stage) : undefined,
      optedInFullReporting,
      reportingType,
    },
    fetchPolicy: 'no-cache',
  });
};

export const updateReportVersionRequest = (
  client: ApolloClient<object>,
  reportId: number | string,
  version?: 'v4' | null,
) => {
  return client.mutate({
    mutation: updateReportVersionMutation,
    variables: {
      reportId,
      version,
    },
    fetchPolicy: 'no-cache',
  });
};

export const updateReportYear = (
  client: ApolloClient<object>,
  reportId: number | string,
  year: number,
) => {
  return client.mutate({
    mutation: mutationUpdateYear,
    variables: {
      reportId,
      year,
    },
    fetchPolicy: 'no-cache',
  });
};

export const updateReportName = (
  client: ApolloClient<object>,
  reportId: number | string,
  name: string,
) => {
  return client.mutate({
    mutation: mutationUpdateReportName,
    variables: {
      reportId,
      name,
    },
    fetchPolicy: 'no-cache',
  });
};

export const updateReportReportingType = (
  client: ApolloClient<object>,
  reportId: number | string,
  reportingType: 'FULL' | 'LIGHT' | 'SUPER_LIGHT',
  status?: FeedbackStatus,
  stage?: ReportStage,
  optedInFullReporting?: boolean,
) => {
  return client.mutate({
    mutation: mutationUpdateReportingType,
    variables: {
      reportId,
      reportingType,
      status,
      stage: stage ? capitalizeOnlyFirstLetter(stage) : undefined,
      optedInFullReporting,
    },
    fetchPolicy: 'no-cache',
  });
};

export const optInForFullReporting = (
  client: ApolloClient<object>,
  reportId: number | string,
  optedInFullReporting = true,
) => {
  return client.mutate({
    mutation: mutationOptInFullReporting,
    variables: {
      reportId,
      optedInFullReporting,
    },
    fetchPolicy: 'no-cache',
  });
};

export const updateReportLevel1DonorName = (
  client: ApolloClient<object>,
  reportId: number | string,
  level1DonorName: string,
) => {
  return client.mutate({
    mutation: mutationUpdateReportLevel1DonorName,
    variables: {
      reportId,
      level1DonorName,
      visibility: level1DonorName ? 'RESTRICTED' : undefined,
    },
    fetchPolicy: 'no-cache',
  });
};

export const updateReportLevel1Budget = (
  client: ApolloClient<object>,
  reportId: number | string,
  level1Budget: string,
) => {
  return client.mutate({
    mutation: mutationUpdateReportLevel1Budget,
    variables: {
      reportId: Number(reportId),
      level1Budget,
    },
    fetchPolicy: 'no-cache',
  });
};

export const updateReportLevel1ReachSucceedMatch = (
  client: ApolloClient<object>,
  reportId: number | string,
  level1ReachSucceedMatch: string,
) => {
  return client.mutate({
    mutation: mutationUpdateReportLevel1ReachSucceedMatch,
    variables: {
      reportId: Number(reportId),
      level1ReachSucceedMatch,
    },
    fetchPolicy: 'no-cache',
  });
};

export interface ReportCustomQuestionsAPI {
  allReportSurvey: SurveyFromAPI[];
}

export const getCustomQuestionsByReportId = (
  client: ApolloClient<object>,
  reportId: number | string,
) => {
  return client.query<ReportCustomQuestionsAPI>({
    query: customQuestionsQuery,
    variables: {
      reportId,
    },
    fetchPolicy: 'network-only',
  });
};
